import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PublicPlatformSettings {
  buyer_fee_enabled: boolean;
  buyer_fee_percentage: number;
  buyer_fee_fixed: number;
  maintenance_mode: boolean;
  featured_placement_price: number;
  max_free_events_per_organizer: number;
  enable_user_signups: boolean;
  enable_organizer_signups: boolean;
  featured_category_ids: string[];
  max_events_per_user: number;
  event_approval_required: boolean;
}

const DEFAULT_SETTINGS: PublicPlatformSettings = {
  buyer_fee_enabled: true,
  buyer_fee_percentage: 5,
  buyer_fee_fixed: 0.99,
  maintenance_mode: false,
  featured_placement_price: 30,
  max_free_events_per_organizer: 3,
  enable_user_signups: true,
  enable_organizer_signups: true,
  featured_category_ids: [],
  max_events_per_user: 1000,
  event_approval_required: false,
};

export const usePlatformSettings = () => {
  const [settings, setSettings] = useState<PublicPlatformSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the restricted public view instead of the full table
      const { data, error: fetchError } = await supabase
        .from('platform_settings_public' as any)
        .select('*')
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        setSettings(data as unknown as PublicPlatformSettings);
      } else {
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (err: any) {
      console.error('Error fetching platform settings:', err);
      setError(err.message);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();

    // Collapse focus + visibilitychange + 5-min interval into a single
    // visibility-aware refresh, throttled to at most once per 60s.
    const MIN_REFRESH_MS = 60 * 1000;
    const PERIODIC_MS = 5 * 60 * 1000;
    let lastRefresh = Date.now();

    const maybeRefresh = (force = false) => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (!force && now - lastRefresh < MIN_REFRESH_MS) return;
      lastRefresh = now;
      void fetchSettings();
    };

    const handleVisibilityChange = () => maybeRefresh();
    const refreshInterval = window.setInterval(() => maybeRefresh(true), PERIODIC_MS);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings,
  };
};

export default usePlatformSettings;
